import React from 'react';
import { Card, Tag, Progress } from 'antd';
import {
  Thermometer,
  Droplet,
  Gauge,
  FlaskConical,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { defaultColors } from '@/themes/antd-theme';
import { formatDateTime } from '@/utils/datetime';

const SensorCard = ({ sensor, latestReading, onViewDetails }) => {
  const getSensorIcon = (category) => {
    const icons = {
      'ENVIRONMENTAL': <Thermometer size={20} />,
      'PROCESS': <Gauge size={20} />,
      'EQUIPMENT': <FlaskConical size={20} />,
      'SAFETY': <AlertTriangle size={20} />,
      'QUALITY': <CheckCircle size={20} />
    };
    return icons[category] || <Gauge size={20} />;
  };

  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': 'success',
      'INACTIVE': 'default',
      'MAINTENANCE': 'warning',
      'FAULTY': 'error',
      'CALIBRATION_DUE': 'warning'
    };
    return colors[status] || 'default';
  };

  const getAlertColor = (level) => {
    const colors = {
      'NORMAL': defaultColors.success,
      'WARNING': defaultColors.warning,
      'CRITICAL': defaultColors.error
    };
    return colors[level] || defaultColors.success;
  };

  const calculatePercentage = () => {
    if (!latestReading || !sensor.min_threshold || !sensor.max_threshold) {
      return 50;
    }
    const value = parseFloat(latestReading.value_numeric);
    const min = parseFloat(sensor.min_threshold);
    const max = parseFloat(sensor.max_threshold);
    return ((value - min) / (max - min)) * 100;
  };

  const alertLevel = latestReading?.alert_level || 'NORMAL';
  const percentage = calculatePercentage();

  return (
    <Card
      className="h-full cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
      style={{
        borderLeft: `4px solid ${getAlertColor(alertLevel)}`
      }}
      onClick={() => onViewDetails(sensor)}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <div style={{ color: getAlertColor(alertLevel) }}>
              {getSensorIcon(sensor.sensor_type_details?.category)}
            </div>
            <div>
              <h6 className="text-base font-semibold m-0">
                {sensor.sensor_name}
              </h6>
              <span className="text-xs text-gray-600">
                {sensor.sensor_code}
              </span>
            </div>
          </div>
          <Tag
            color={getStatusColor(sensor.status)}
          >
            {sensor.status}
          </Tag>
        </div>

        <div className="mb-4">
          <p className="text-xs text-gray-600 mb-0">
            {sensor.sensor_type_details?.name}
          </p>
          {sensor.equipment_details && (
            <p className="text-xs text-gray-600 mb-0">
              Equipment: {sensor.equipment_details.equipment_name}
            </p>
          )}
        </div>

        {latestReading ? (
          <>
            <div className="flex items-baseline gap-2 mb-2">
              <h4 className="text-3xl font-bold m-0">
                {parseFloat(latestReading.value_numeric).toFixed(2)}
              </h4>
              <span className="text-sm text-gray-600">
                {sensor.sensor_type_details?.unit_of_measurement}
              </span>
            </div>

            {sensor.min_threshold && sensor.max_threshold && (
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-gray-600">
                    Min: {sensor.min_threshold}
                  </span>
                  <span className="text-xs text-gray-600">
                    Max: {sensor.max_threshold}
                  </span>
                </div>
                <Progress
                  percent={Math.min(Math.max(percentage, 0), 100)}
                  strokeColor={getAlertColor(alertLevel)}
                  showInfo={false}
                  strokeWidth={8}
                />
              </div>
            )}

            <p className="text-xs text-gray-600 mb-0">
              Last updated: {formatDateTime(latestReading.timestamp)}
            </p>

            {alertLevel !== 'NORMAL' && (
              <Tag
                icon={alertLevel === 'CRITICAL' ? <AlertCircle size={14} /> : <AlertTriangle size={14} />}
                color={alertLevel === 'CRITICAL' ? 'error' : 'warning'}
                className="mt-2"
              >
                {alertLevel} Alert
              </Tag>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 mb-0">
              No data available
            </p>
          </div>
        )}

        {sensor.is_calibration_due && (
          <Tag
            icon={<AlertTriangle size={14} />}
            color="warning"
            className="mt-2"
          >
            Calibration Due
          </Tag>
        )}
      </div>
    </Card>
  );
};

export default SensorCard;
